import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent';

const deliverables = [
  "Developed a comprehensive car rental management system",
  "Implemented reservation management for seamless booking",
  "Integrated maintenance tracking to ensure vehicle upkeep",
  "Automated payment processing for rentals",
  "Streamlined repair scheduling and management",
  "Built an admin dashboard to manage user information, contacts, discounts, categories, vehicle classes, models, and branches",
  "Enabled advanced reporting and analytics for business insights",
  "Ensured system scalability and performance optimization",
  "Developed a mobile application for on-the-go management"
];

const RDRental = () => {
  const features = [
    {
      title: "Comprehensive Car Rental Management System",
      description: `RD Rental isn’t just software — it’s your business command center. 
    Designed to manage every moving part of your car rental operation, it brings together vehicle tracking, customer management, reservations, maintenance, and payments under one powerful dashboard. 
    No more juggling spreadsheets or disconnected tools — everything you need is right at your fingertips.`,
      points: [
        "Centralized control for all locations and branches",
        "Eliminates manual work and costly miscommunication",
        "Keeps your entire operation running seamlessly"
      ]
    },
    {
      title: "Effortless Reservation Management",
      description: `Never lose another booking or disappoint a customer. RD Rental ensures smooth, real-time reservations that eliminate double-bookings and confusion. 
    Staff can manage bookings effortlessly, while customers enjoy a quick and reliable reservation process — from pick-up to drop-off.`,
      points: [
        "Live vehicle availability and instant confirmations",
        "Easy booking updates, cancellations, and reassignments",
        "Smart prioritization for high-demand vehicles"
      ],
      impact: "Boosts efficiency, reduces human error, and increases customer trust."
    },
    {
      title: "Maintenance Tracking Made Simple",
      description: `Keep your fleet in excellent condition and ensure smooth operations every day. 
  RD Rental allows you to set and manage maintenance rules for your vehicles with ease. 
  Whether it’s routine servicing or major repairs, all maintenance activities are tracked and recorded for full visibility.`,
      points: [
        "Customizable maintenance rules based on company policies",
        "Digital log of all service and repair history",
        "Clear visibility into pending and completed maintenance tasks",
      ],
      impact: "Better upkeep, improved vehicle reliability, and greater peace of mind for your operations team."
    },
    {
      title: "Payment Processing",
      description: `Simplify your rental billing and payment management with RD Rental. 
  The platform allows you to handle invoices, receipts, and payment records all in one place. 
  Whether your customers pay by card, cash, or online transfer, every transaction is handled securely and recorded for complete transparency.`,
      points: [
        "Integrated payment gateway for safe and reliable transactions",
        "Quick invoice and receipt generation",
        "Manual control for sending payment reminders and managing overdue accounts",
      ],
      impact: "Streamlined payment handling, reduced administrative effort, and greater financial accuracy."
    },
    {
      title: "Repair Management & Tracking",
      description: `Accidents and repairs are part of every rental operation — but managing them doesn’t have to be complicated. 
  RD Rental helps you record, assign, and monitor every repair order in one centralized system. 
  Create repair jobs manually, update progress, and control repair expenses with complete visibility.`,
      points: [
        "Manually create and log repair orders for any vehicle",
        "Assign repair tasks to internal teams or external workshops",
        "Track progress and maintain detailed repair history",
        "Link repair expenses with cost reports for better financial insight"
      ],
      impact: "Reduces operational confusion, improves repair tracking, and ensures your vehicles stay road-ready with minimal effort."
    },
    {
      title: "Powerful Admin Dashboard",
      description: `Take full control of your business from one beautifully designed dashboard. 
    The Admin Dashboard brings together everything — users, vehicles, reservations, payments, and branches — with crystal-clear data insights and easy-to-use tools.`,
      points: [
        "Manage staff roles, permissions, and access levels",
        "Monitor customer activity and communications",
        "Set up discounts",
        "Control vehicle listings, models, and categories",
        "Handle multiple branches effortlessly"
      ],
      impact: "A single hub for smarter management and faster decision-making."
    },
    {
      title: "Scalable and Future-Ready System",
      description: `Whether you manage ten cars or ten thousand, RD Rental grows with you. 
    Built on a robust, high-performance architecture, the system ensures speed, reliability, and flexibility — no matter how fast your business expands.`,
      points: [
        "Easily add more vehicles, branches, or users",
        "Optimized performance for heavy traffic and data loads",
        "Supports custom integrations and future modules",
        "Engineered for stability during business growth"
      ],
      impact: "Expand confidently without worrying about system limits."
    },
    {
      title: "RD Rental Mobile App — Manage Your Fleet on the Move",
      description: `Your operations don’t stop when you step out of the office — and neither should your control. 
  With the RD Rental Mobile App, your team can manage daily rental tasks directly from their smartphones or tablets. 
  From vehicle check-ins and checkouts to customer signatures and real-time pickup and return tracking, everything you need to stay on top of your fleet is just a tap away.`,
      points: [
        "Check-in and check-out vehicles instantly from the mobile app",
        "Track pickups, returns, and ongoing rentals in one place",
        "Capture digital rental agreement signatures from customers",
        "Monitor fleet and booking status on the go",
        "Designed for both managers and field staff for complete visibility"
      ],
      impact: "Empowers your team with mobility, boosts productivity, and ensures every rental runs smoothly — wherever you are."
    },
    {
      title: "Customer Booking Website — Seamless Rentals",
      description: `Let your customers book their next ride directly from your website — anytime, anywhere. 
  RD Rental powers a fully integrated, customer-facing booking platform that connects seamlessly with your backend system. 
  Every reservation made online updates instantly in your RD Rental dashboard, giving you full visibility over customer activity, vehicle availability, and payments.`,
      points: [
        "Professional, user-friendly booking website for your customers",
        "Instant synchronization with RD Rental’s reservation system",
        "Accept online payments securely and confirm bookings immediately",
        "Showcase available vehicles, pricing, and special offers in real time",
        "Collect customer details, rental preferences, and agreements digitally"
      ],
      impact: "Gives customers a smooth, self-service experience while keeping your team in full control — driving more bookings and building stronger brand trust."
    }
  ];

  return (
    <>
      <Helmet>
        <title>RD Rental - Car Rental & Fleet Management Software by Data Repo</title>
        <meta
          name="description"
          content="RD Rental is a powerful car rental and fleet management software developed by Data Repo. Manage bookings, vehicle maintenance, payments, repairs, branches, and customer reservations with one scalable, all-in-one platform."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="hero--container relative flex flex-col justify-center w-full h-[60vh] bg-no-repeat bg-fixed md:bg-origin-border md:h-[80vh] pt-[65px]"
      >
        <h1 className="text-center text-white text-2xl md:text-5xl font-bold uppercase">RD RENTAL</h1>
        <div className="row--image-style bg-transparent w-full absolute bottom-0 left-0 right-0">
          <svg className="waves w-full h-44" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 300" preserveAspectRatio="none">
            <path fill="rgb(226 232 240)" d="M 1000 280 l 2 -253 c -155 -36 -310 135 -415 164 c -102.64 28.35 -149 -32 -235 -31 c -80 1 -142 53 -229 80 c -65.54 20.34 -101 15 -126 11.61 v 54.39 z"></path>
            <path fill="rgb(100 116 139)" d="M 1000 261 l 2 -222 c -157 -43 -312 144 -405 178 c -101.11 33.38 -159 -47 -242 -46 c -80 1 -153.09 54.07 -229 87 c -65.21 25.59 -104.07 16.72 -126 16.61 v 22.39 z"></path>
            <path fill="#E5E7EB" d="M 1000 300 l 1 -230.29 c -217 -12.71 -300.47 129.15 -404 156.29 c -103 27 -174 -30 -257 -29 c -80 1 -130.09 37.07 -214 70 c -61.23 24 -108 15.61 -126 10.61 v 22.39 z"></path>
          </svg>
        </div>
      </section>

      <section className="w-full pt-10 px-8 bg-gradient-to-r from-blue-100 via-white to-blue-100 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-200 rounded-full opacity-30 filter blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-200 rounded-full opacity-20 filter blur-3xl"></div>

        <div className="flex flex-col md:flex-row items-center justify-center px-16 relative z-10">
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/rd-rental-new.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/rd-rental-reservations.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full items-center justify-center bg-gray-200">
        <div className="max-w-screen-lg mx-auto py-8">
          <div className="px-8 md:px-1 md:pr-2 py-6">
            <div className="mb-4 text-lg font-normal text-center md:text-left text-gray-600">
              <p><strong>Industry:</strong> Car Rental; Fleet Management; Software</p>
              <p><strong>Location:</strong> USA</p>
              <p><strong>Areas of expertise:</strong> Back-end; Front-end; Quality assurance; Project management; AI</p>
              <p><strong>Timeline:</strong> 2017 - Present</p>
            </div>
          </div>

          <p className="text-xl font-normal text-center md:text-left text-gray-600">
            Developed by Data Repo, RD Rental is a comprehensive car rental software solution that allows businesses to manage reservations, maintenance, payments, and repairs seamlessly. The software also includes an admin dashboard for managing user information, contacts, discounts, categories, vehicle classes, models, and branches.
          </p>
        </div>
      </section>
      <section className="w-full bg-gray-100 py-8">
        <div className="max-w-screen-lg mx-auto px-8 md:px-4">
          <div className="pt-8">
            <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
              Product Development and Enhancement
            </h2>
            <p className="text-xl font-normal text-center md:text-left text-gray-600">
              RD Rental simplifies the car rental process by providing an all-in-one platform for managing various aspects of the business. From reservation management and payment processing to maintenance tracking and repair scheduling, the software covers it all. The admin dashboard allows for easy management of users, vehicles, and other essential business components.
            </p>
          </div>

          <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
            Complete Fleet Control — Manage Every Vehicle with Ease
          </h2>
          <p className="text-xl font-normal text-center md:text-left text-gray-600 mb-5">
            RD Rental gives you complete oversight of your entire fleet from a single, intuitive dashboard.
            Track vehicle availability, monitor usage, and manage branches effortlessly, ensuring smooth operations
            and maximum efficiency. Say goodbye to manual tracking and fragmented systems.

            See below all the features that make RD Rental the ultimate car rental management solution.
          </p>

          {features.map((feature, index) => (
            <div key={index} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-black py-3">{index + 1}. {feature.title}</h2>
              <p className="text-xl text-gray-700 py-2">{feature.description}</p>
              {feature.points && (
                <ul className="list-disc pl-5 text-lg text-gray-600 py-2">
                  {feature.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              )}
              {feature.impact && <p className="text-lg text-gray-600 font-semibold mt-2">{feature.impact}</p>}
            </div>
          ))}
        </div>
      </section>
      <section className="w-full bg-gray-200 py-8">
        <div className="max-w-screen-lg mx-auto px-8 md:px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-black py-5">Key Deliverables</h2>
          <ul className="list-disc pl-5 text-xl font-normal text-gray-600">
            {deliverables.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>
      <ContactUsComponent />
    </>
  );
}

export default RDRental;